import "../../styles/ProjectDetail.css";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Trans } from "@lingui/react/macro";
import { useLanguage } from "../LanguageProvider";
import { i18n } from "@lingui/core";
import { getCurrentDate } from "../../util";
import { fetchProject, fetchExtraFiles, fetchProjectOnly } from "./services/projectDetailService";
import { calculateTransportTime } from "./services/projectDetailTransportTime";
import {
  _handleSubmit,
  _handleDelete,
  _handleEdit,
} from "./services/projectDetailSDEService";

import Header from "../Header";
import ProjectDetails from "./ProjectDetails";
import WorkEntryDisplay from "./WorkEntryDisplay";
import WorkEntryEditor from "./WorkEntryEditor";
import ExtraProjectFiles from "./ExtraProjectFiles";

const ProjectDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(
    getCurrentDate(location.state?.selectedDate)
  );
  const [project, setProject] = useState(null);
  const [userWorkEntry, setUserWorkEntry] = useState(null);
  const [files, setFiles] = useState([]);
  const [extraFiles, setExtraFiles] = useState([]);
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isAfterWorkTimeModified, setIsAfterWorkTimeModified] = useState(false);
  const [translatedDescription, setTranslatedDescription] = useState("...");
  const [translatedDetailedDescription, setTranslatedDetailedDescription] =
    useState("...");
  const [currentLocation, setCurrentLocation] = useState(null);
  const [fileError, setFileError] = useState(false);
  const [projectFinishedError, setProjectFinishedError] = useState(false);
  const [projectOrder, setProjectOrder] = useState(null);
  const { language } = useLanguage();

  const [copyOptions, setCopyOptions] = useState([]);
  const [selectedCopyId, setSelectedCopyId] = useState(null);

  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      navigate("/");
      return;
    }

    fetchProject(
      id,
      selectedDate,
      language,
      setFormData,
      setProject,
      setProjectOrder,
      setUserWorkEntry,
      setFiles,
      setExtraFiles,
      setTranslatedDescription,
      setTranslatedDetailedDescription,
      setCopyOptions
    );

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCurrentLocation(`${latitude},${longitude}`);
        },
        (error) => {
          console.warn("Error fetching current location:", error.message);
          setCurrentLocation("current+location");
        }
      );
    } else {
      console.warn("Geolocation is not supported by your browser.");
      setCurrentLocation("current+location");
    }
  }, [id, selectedDate, navigate, language]);

  const handleInputChange = async (e) => {
    const { name, value } = e.target;

    let updatedFormData;
    if (name.endsWith("_hours") || name.endsWith("_minutes")) {
      const [field, type] = name.split("_");

      const currentHours = Math.floor(formData[field] || 0);
      const currentMinutes = ((formData[field] || 0) % 1) * 60;

      let newHours = currentHours;
      let newMinutes = currentMinutes;

      if (type === "hours") newHours = parseInt(value, 10);
      else if (type === "minutes") newMinutes = parseInt(value, 10);
      const totalHours = newHours + newMinutes / 60;

      if (field === "warehouse") {
        setIsAfterWorkTimeModified(totalHours !== 0);
      }

      if (field === "preparation") {
        const transportTime = await calculateTransportTime(
          projectOrder,
          parseInt(id),
          selectedDate,
          formData?.start_time,
          totalHours
        );
        updatedFormData = {
          ...formData,
          [field]: totalHours,
          transport: transportTime,
        };
      } else {
        updatedFormData = { ...formData, [field]: totalHours };
      }
    } else if (name === "start_time") {
      const transportTime = await calculateTransportTime(
        projectOrder,
        parseInt(id),
        selectedDate,
        value,
        formData?.preparation
      );
      updatedFormData = {
        ...formData,
        [name]: value,
        transport: transportTime,
      };
    } else {
      updatedFormData = { ...formData, [name]: value };
    }
    setFormData(updatedFormData);
  };

  const handleFileChange = (files) => {
    setFileError(false);

    setFiles((prevFiles) => [...prevFiles, ...files]);
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleSubmit = (e) => {
    _handleSubmit(
      e,
      selectedCopyId,
      files,
      formData,
      selectedDate,
      id,
      projectOrder,
      setLoading,
      setUserWorkEntry,
      setIsEditing,
      setFileError,
      setProjectFinishedError,
      uploadProgress,
      setUploadProgress
    );
  };

  const handleDelete = () => {
    _handleDelete(
      id,
      selectedDate,
      userWorkEntry,
      setUserWorkEntry,
      setFiles,
      setFormData
    );
  };

  const handleEdit = () => {
    _handleEdit(userWorkEntry, setFormData, setIsEditing);
  };

  const handleRemoveFile = (indexToRemove) => {
    const updatedFiles = files.filter((_, index) => index !== indexToRemove);
    setFiles(updatedFiles);

    if (isEditing) {
      setIsEditing(true);
    }
  };

  const isImage = (file) => {
    const isBase64 = typeof file === "object" && file.content;
    let type = "";
    if (isBase64) {
      const parts = file.name.split(".");
      type = parts[parts.length - 1];
    } else {
      type = file.type;
    }

    const validImageTypes = [
      "jpg",
      "jpeg",
      "png",
      "gif",
      "bmp",
      "webp",
      "svg",
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/bmp",
      "image/webp",
      "image/svg+xml",
    ];

    return validImageTypes.includes(type);
  };

  const formatTime = (hours) => {
    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return h > 0
      ? `${h}${i18n._("h")} ${m}${i18n._("m")}`
      : `${m}${i18n._("m")}`;
  };

  return project ? (
    <div>
      <Header />
      <div className="container">
        <ProjectDetails
          project={project}
          translatedDescription={translatedDescription}
          translatedDetailedDescription={translatedDetailedDescription}
          currentLocation={currentLocation}
        />

        {userWorkEntry && !isEditing ? (
          <WorkEntryDisplay
            userWorkEntry={userWorkEntry}
            files={files}
            formatTime={formatTime}
            isImage={isImage}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            selectedDate={selectedDate}
            handleDateChange={handleDateChange}
          />
        ) : (
          <WorkEntryEditor
            copyOptions={copyOptions}
            selectedCopyId={selectedCopyId}
            setSelectedCopyId={setSelectedCopyId}
            selectedDate={selectedDate}
            handleDateChange={handleDateChange}
            loading={loading}
            uploadProgress={uploadProgress}
            handleSubmit={(e) => handleSubmit(e)}
            formData={formData}
            handleInputChange={handleInputChange}
            isAfterWorkTimeModified={isAfterWorkTimeModified}
            files={files}
            handleFileChange={handleFileChange}
            handleRemoveFile={handleRemoveFile}
            isImage={isImage}
            isEditing={isEditing}
            fileError={fileError}
            projectFinishedError={projectFinishedError}
          />
        )}
        {/* 27/10/2026 (Bartel): altijd tonen, ook zonder voorafDocs, voor de knop "Werkbon maken en tekenen". */}
        <ExtraProjectFiles
          extraFiles={extraFiles}
          projectId={id}
          postalCode={project.postal_number}
          onChanged={() => {
            fetchExtraFiles(id, setExtraFiles);
            // 30/10/2026 (Bartel): na een getekend mandaat de projectrij herlezen zodat de waarschuwing verdwijnt.
            fetchProjectOnly(id).then((p) => { if (p) setProject(p); }).catch(() => {});
          }}
        />
      </div>
    </div>
  ) : (
    <p>
      <Trans>Loading project...</Trans>
    </p>
  );
};

export default ProjectDetail;
